/**
 * AdminQuotePool.jsx  —  Admin · Quote Pool
 * Design system: #0c1118 dark · #f9ab14 amber · Bebas Neue + DM Sans + DM Mono
 * Inline styles only — no Tailwind dependency.
 */

// src/pages/admin/AdminQuotePool.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FileText, RefreshCw, CheckCircle, Clock, XCircle, Send,
  ThumbsUp, ThumbsDown, Upload, Download, AlertCircle,
  User, Calendar, Phone, Mail, MapPin, MessageSquare,
  ChevronDown, ChevronUp, Building,
} from "lucide-react";

/* ── Design tokens ─────────────────────────────────────────────── */
const T = {
  fontDisplay: "'DM Sans', sans-serif",
  fontBody:    "'DM Sans', sans-serif",
  fontMono: "'DM Sans', sans-serif",
  amber:       "#111111",
  amberDark:   "#333333",
  amberBg:     "rgba(0,0,0,0.06)",
  amberBorder: "rgba(0,0,0,0.12)",
  dark:        "#0c1118",
  white:       "#ffffff",
  bg:          "#f4f6f8",
  surface:     "#ffffff",
  border:      "#e8ecf0",
  muted:       "#6b7280",
  mutedLight:  "#9ca3af",
};

/* ── Inject styles once ─────────────────────────────────────────── */
if (typeof document !== "undefined" && !document.getElementById("aqpl-styles")) {
  const s = document.createElement("style");
  s.id = "aqpl-styles";
  s.textContent = `
    @keyframes aqpl-spin    { to { transform: rotate(360deg); } }
    @keyframes aqpl-fade-up { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
    .aqpl-spin { animation: aqpl-spin 0.85s linear infinite; }
    .aqpl-row:hover { background: rgba(0,0,0,0.03) !important; }
    .aqpl-btn:hover { opacity: 0.82; }
    .aqpl-expand-row { background: rgba(12,17,24,0.025); }
    .aqpl-drop:hover  { border-color: #111111 !important; background: rgba(0,0,0,0.03) !important; }
    .aqpl-card { transition: box-shadow 0.15s; }
    .aqpl-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.1) !important; }
  `;
  document.head.appendChild(s);
}

/* ── Mobile detection ───────────────────────────────────────────── */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const BASE = process.env.REACT_APP_BACKEND_URL || "";

async function jfetch(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...opts,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`${res.status} ${res.statusText}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  return res.json();
}

/* ── Status config ──────────────────────────────────────────────── */
const STATUS_CONFIG = {
  draft:          { bg: "rgba(100,116,139,0.1)",  color: "#475569",  border: "rgba(100,116,139,0.2)",  Icon: Clock,       label: "Draft" },
  pending_review: { bg: "rgba(0,0,0,0.06)",        color: "#333333",  border: "rgba(0,0,0,0.12)",       Icon: Clock,       label: "Pending Review" },
  approved:       { bg: "rgba(34,197,94,0.1)",    color: "#16a34a",  border: "rgba(34,197,94,0.25)",   Icon: CheckCircle, label: "File Ready" },
  rejected:       { bg: "rgba(239,68,68,0.1)",    color: "#dc2626",  border: "rgba(239,68,68,0.25)",   Icon: XCircle,     label: "Rejected" },
  sent_to_client: { bg: "rgba(99,102,241,0.1)",   color: "#4338ca",  border: "rgba(99,102,241,0.2)",   Icon: Send,        label: "Sent to Client" },
  accepted:       { bg: "rgba(34,197,94,0.1)",    color: "#15803d",  border: "rgba(34,197,94,0.2)",    Icon: ThumbsUp,    label: "Accepted" },
  declined:       { bg: "rgba(239,68,68,0.08)",   color: "#b91c1c",  border: "rgba(239,68,68,0.2)",    Icon: ThumbsDown,  label: "Declined" },
};

function StatusBadge({ s }) {
  const c = STATUS_CONFIG[s] || { bg: "rgba(107,114,128,0.1)", color: T.muted, border: "rgba(107,114,128,0.2)", Icon: AlertCircle, label: s };
  const Icon = c.Icon;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 99,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      fontFamily: T.fontBody, fontSize: "0.65rem",
      fontWeight: 600, letterSpacing: "0.06em", 
      whiteSpace: "nowrap",
    }}>
      <Icon size={11} />
      {c.label || String(s || "").replaceAll("_", " ")}
    </span>
  );
}

/* ── Toast ──────────────────────────────────────────────────────── */
function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, animation: "aqpl-fade-up 0.2s ease" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "12px 20px", borderRadius: 10, maxWidth: 380,
        background: toast.type === "success" ? "#16a34a" : "#dc2626",
        color: T.white, boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        fontFamily: T.fontBody, fontSize: "0.88rem", fontWeight: 600,
      }}>
        {toast.type === "success" ? <CheckCircle size={16} style={{ flexShrink: 0 }} /> : <AlertCircle size={16} style={{ flexShrink: 0 }} />}
        {toast.message}
      </div>
    </div>
  );
}

/* ── Upload Modal ───────────────────────────────────────────────── */
function UploadModal({ quote, onClose, onSuccess, showToast }) {
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${BASE}/api/quotes/${quote.id}/upload`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error("Upload failed");
      showToast('PDF uploaded. Now click "Send to Client" to notify the user.');
      onSuccess();
      onClose();
    } catch {
      showToast("Upload failed. Please try again.", "error");
    } finally {
      setUploading(false);
    }
  }

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)",
    }}>
      <div style={{
        background: T.surface, borderRadius: 14,
        boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
        width: "100%", maxWidth: 440, margin: "0 16px",
        overflow: "hidden",
        border: `1px solid ${T.border}`,
      }}>
        {/* Header */}
        <div style={{
          padding: "18px 22px",
          borderBottom: `1px solid ${T.border}`,
          background: T.dark,
        }}>
          <h2 style={{ fontFamily: T.fontBody, fontSize: "1.3rem", letterSpacing: "0.04em", color: T.white, margin: 0 }}>
            Upload Quote PDF
          </h2>
          <p style={{ fontFamily: T.fontBody, fontSize: "0.8rem", color: T.mutedLight, margin: "4px 0 0" }}>
            For <span style={{ color: "#ffffff", fontWeight: 600 }}>{quote.project?.name || quote.projectId}</span>.{" "}
            After uploading, click <strong style={{ color: T.white }}>Send to Client</strong> to notify them.
          </p>
        </div>

        {/* Drop zone */}
        <div style={{ padding: "20px 22px" }}>
          <div
            className="aqpl-drop"
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${T.border}`,
              borderRadius: 10, padding: "32px 16px",
              textAlign: "center", cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            <Upload size={36} style={{ color: T.mutedLight, margin: "0 auto 10px", display: "block" }} />
            {file ? (
              <p style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, margin: 0 }}>{file.name}</p>
            ) : (
              <>
                <p style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, margin: 0 }}>Click to select file</p>
                <p style={{ fontFamily: T.fontBody, fontSize: "0.68rem", color: T.muted, margin: "6px 0 0", letterSpacing: "0.05em" }}>
                  PDF · DOC · DOCX · CSV · XLS · XLSX
                </p>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx,.csv,.xls,.xlsx"
              style={{ display: "none" }}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 22px",
          borderTop: `1px solid ${T.border}`,
          display: "flex", justifyContent: "flex-end", gap: 10,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "8px 18px", borderRadius: 8,
              background: "transparent", border: `1px solid ${T.border}`,
              fontFamily: T.fontBody, fontSize: "0.84rem", fontWeight: 600,
              color: T.muted, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "8px 18px", borderRadius: 8,
              background: uploading || !file ? T.mutedLight : T.dark,
              border: "none",
              fontFamily: T.fontBody, fontSize: "0.84rem", fontWeight: 700,
              color: "#ffffff", cursor: !file || uploading ? "not-allowed" : "pointer",
            }}
          >
            {uploading ? <RefreshCw size={14} className="aqpl-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── Expanded detail panel (div version for mobile cards) ───────── */
function QuoteDetailPanel({ q }) {
  const ci = q.contactInfo || {};
  const labelStyle = {
    fontFamily: T.fontBody, fontSize: "0.59rem", letterSpacing: "0.1em",
     color: T.muted, fontWeight: 700, margin: "0 0 10px",
  };
  const rowStyle = {
    display: "flex", alignItems: "flex-start", gap: 8,
    fontFamily: T.fontBody, fontSize: "0.82rem", color: T.dark, marginBottom: 6,
  };

  return (
    <div style={{
      background: T.bg, border: `1px solid ${T.border}`,
      borderRadius: 10, padding: "18px 20px", margin: "0 0 8px",
      display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20,
    }}>
      {/* Contact */}
      <div>
        <p style={labelStyle}>Contact Details</p>
        <div style={rowStyle}>
          <User size={13} style={{ color: T.mutedLight, flexShrink: 0, marginTop: 2 }} />
          <span>{[ci.firstName, ci.lastName].filter(Boolean).join(" ") || "—"}</span>
        </div>
        {ci.email && (
          <div style={rowStyle}>
            <Mail size={13} style={{ color: T.mutedLight, flexShrink: 0, marginTop: 2 }} />
            <a href={`mailto:${ci.email}`} style={{ color: T.amber, textDecoration: "none", fontWeight: 600 }}>{ci.email}</a>
          </div>
        )}
        {ci.phone && (
          <div style={rowStyle}>
            <Phone size={13} style={{ color: T.mutedLight, flexShrink: 0, marginTop: 2 }} />
            <span>{ci.phone}</span>
          </div>
        )}
        {(ci.address || ci.city) && (
          <div style={rowStyle}>
            <MapPin size={13} style={{ color: T.mutedLight, flexShrink: 0, marginTop: 2 }} />
            <span>{[ci.address, ci.city, ci.state, ci.zip].filter(Boolean).join(", ")}</span>
          </div>
        )}
      </div>

      {/* Building */}
      <div>
        <p style={labelStyle}>About the Building</p>
        <div style={rowStyle}>
          <MessageSquare size={13} style={{ color: T.mutedLight, flexShrink: 0, marginTop: 2 }} />
          <span style={{ whiteSpace: "pre-wrap" }}>
            {ci.buildingDescription || <em style={{ color: T.mutedLight }}>No description provided</em>}
          </span>
        </div>
      </div>

      {/* Project */}
      <div>
        <p style={labelStyle}>Project</p>
        <div style={rowStyle}>
          <Building size={13} style={{ color: T.mutedLight, flexShrink: 0, marginTop: 2 }} />
          <span style={{ fontWeight: 600 }}>{q.project?.name || q.projectId}</span>
        </div>
        <div style={rowStyle}>
          <Calendar size={13} style={{ color: T.mutedLight, flexShrink: 0, marginTop: 2 }} />
          <span style={{ color: T.muted }}>Submitted {new Date(q.createdAt).toLocaleString()}</span>
        </div>
        {q.fileUrl && (
          <a
            href={q.fileUrl} target="_blank" rel="noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              color: T.amber, fontWeight: 700, textDecoration: "none",
              fontFamily: T.fontBody, fontSize: "0.82rem", marginTop: 4,
            }}
          >
            <Download size={13} /> Download current quote
          </a>
        )}
      </div>
    </div>
  );
}

/* ── Expanded detail row (table version for desktop) ────────────── */
function QuoteDetailRow({ q }) {
  return (
    <tr>
      <td colSpan={7} style={{ padding: "0 20px" }}>
        <QuoteDetailPanel q={q} />
      </td>
    </tr>
  );
}

/* ── Mobile quote card ──────────────────────────────────────────── */
function QuoteMobileCard({ q, isExpanded, onToggle, onUpload, onSend, busy }) {
  const ci      = q.contactInfo || {};
  const fullName = [ci.firstName, ci.lastName].filter(Boolean).join(" ") || ci.email || "Unknown";
  const hasFile  = !!q.fileUrl;
  const isSent   = q.status === "sent_to_client" || q.status === "accepted";

  return (
    <div style={{ marginBottom: 8 }}>
      <div
        className="aqpl-card"
        style={{
          background: T.surface, borderRadius: 10,
          border: `1px solid ${isExpanded ? T.amberBorder : T.border}`,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {/* Card header row */}
        <div style={{ padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 10 }}>
          {/* Avatar */}
          <div style={{
            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
            background: T.bg, border: `1px solid ${T.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <User size={15} style={{ color: T.muted }} />
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontFamily: T.fontBody, fontWeight: 700, fontSize: "0.88rem", color: T.dark }}>
                {q.project?.name || q.projectId}
              </span>
              <StatusBadge s={q.status} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
              <span style={{ fontFamily: T.fontBody, fontSize: "0.78rem", color: T.muted }}>{fullName}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontFamily: T.fontBody, fontSize: "0.68rem", color: T.mutedLight }}>
                <Calendar size={11} />
                {new Date(q.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Expand toggle */}
          <button
            onClick={onToggle}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28, borderRadius: 6, flexShrink: 0,
              background: isExpanded ? T.amberBg : T.bg,
              border: `1px solid ${isExpanded ? T.amberBorder : T.border}`,
              cursor: "pointer", color: isExpanded ? T.amber : T.muted,
            }}
          >
            {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        </div>

        {/* Action buttons */}
        <div style={{
          padding: "10px 14px",
          borderTop: `1px solid ${T.border}`,
          background: T.bg,
          display: "flex", gap: 8, flexWrap: "wrap",
        }}>
          {/* Download link */}
          {hasFile && (
            <a
              href={q.fileUrl} target="_blank" rel="noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "6px 12px", borderRadius: 6,
                background: T.surface, border: `1px solid ${T.border}`,
                fontFamily: T.fontBody, fontSize: "0.77rem", fontWeight: 600,
                color: T.amber, textDecoration: "none",
              }}
            >
              <Download size={12} /> PDF
            </a>
          )}

          {/* Upload */}
          <button
            onClick={onUpload}
            disabled={busy}
            className="aqpl-btn"
            style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "6px 12px", borderRadius: 6,
              background: T.surface, color: T.dark,
              border: `1px solid ${T.border}`,
              fontFamily: T.fontBody, fontSize: "0.77rem", fontWeight: 600,
              cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1,
            }}
          >
            <Upload size={12} />
            {hasFile ? "Replace" : "Upload PDF"}
          </button>

          {/* Send to Client */}
          {hasFile && !isSent && (
            <button
              onClick={onSend}
              disabled={busy}
              className="aqpl-btn"
              style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "6px 12px", borderRadius: 6,
                background: T.dark, color: T.white,
                border: `1px solid ${T.dark}`,
                fontFamily: T.fontBody, fontSize: "0.77rem", fontWeight: 700,
                cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1,
              }}
            >
              {busy ? <RefreshCw size={12} className="aqpl-spin" /> : <Send size={12} />}
              Send to Client
            </button>
          )}

          {/* Resend */}
          {hasFile && isSent && (
            <button
              onClick={onSend}
              disabled={busy}
              className="aqpl-btn"
              style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "6px 12px", borderRadius: 6,
                background: T.surface, color: T.muted,
                border: `1px solid ${T.border}`,
                fontFamily: T.fontBody, fontSize: "0.77rem", fontWeight: 600,
                cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1,
              }}
            >
              {busy ? <RefreshCw size={12} className="aqpl-spin" /> : <Send size={12} />}
              Resend
            </button>
          )}
        </div>
      </div>

      {/* Expanded detail panel */}
      {isExpanded && <QuoteDetailPanel q={q} />}
    </div>
  );
}

const FILTERS = [
  { value: "all",            label: "All" },
  { value: "pending_review", label: "Pending Review" },
  { value: "approved",       label: "File Ready" },
  { value: "sent_to_client", label: "Sent to Client" },
];

/* ── Main component ─────────────────────────────────────────────── */
export default function AdminQuotePool() {
  const isMobile = useIsMobile();
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filter,       setFilter]       = useState("all");
  const [expandedId,   setExpandedId]   = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [toast,        setToast]        = useState(null);
  const [actionBusy,   setActionBusy]   = useState({});

  function showToast(message, type = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }

  async function load() {
    setLoading(true);
    try {
      const r = await jfetch("/api/quotes/admin");
      setItems(r.items || []);
    } catch {
      showToast("Failed to load quotes.", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function updateStatus(quoteId, status) {
    setActionBusy((b) => ({ ...b, [quoteId]: true }));
    try {
      await jfetch(`/api/quotes/${quoteId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      await load();
      showToast(`Status updated to "${status.replaceAll("_", " ")}".`);
    } catch {
      showToast("Failed to update status.", "error");
    } finally {
      setActionBusy((b) => ({ ...b, [quoteId]: false }));
    }
  }

  async function sendToClient(quoteId) {
    setActionBusy((b) => ({ ...b, [quoteId]: true }));
    try {
      await jfetch(`/api/quotes/${quoteId}/mark-sent`, { method: "POST" });
      await load();
      showToast("Quote sent to client — they will receive an email with the download link.");
    } catch (err) {
      let bodyMsg = "";
      try { bodyMsg = JSON.parse(err.body || "{}").message; } catch {}
      if (bodyMsg === "no_file_uploaded_yet") {
        showToast("Upload a PDF file first before sending to client.", "error");
      } else {
        showToast("Failed to send quote to client.", "error");
      }
    } finally {
      setActionBusy((b) => ({ ...b, [quoteId]: false }));
    }
  }

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((q) => q.status === filter);
  }, [items, filter]);

  const counts = useMemo(() => ({
    total:          items.length,
    pending_review: items.filter((q) => q.status === "pending_review").length,
    approved:       items.filter((q) => q.status === "approved").length,
    sent_to_client: items.filter((q) => q.status === "sent_to_client" || q.status === "accepted").length,
  }), [items]);

  const KPI = [
    { label: "Total",          value: counts.total,          accent: T.dark },
    { label: "Pending Review", value: counts.pending_review, accent: T.amber },
    { label: "File Ready",     value: counts.approved,       accent: "#16a34a" },
    { label: "Sent to Client", value: counts.sent_to_client, accent: "#4338ca" },
  ];

  return (
    <div style={{ fontFamily: T.fontBody, padding: isMobile ? "0 0 24px" : 0 }}>

      {/* ── Header ──────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12, marginBottom: 20,
      }}>
        <div>
          <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 700, color: "#0c1118", margin: 0, lineHeight: 1.2 }}>
            Quote Pool
          </h1>
          {!isMobile && (
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>
              Individual user quote requests — upload PDFs and send to clients
            </p>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="aqpl-btn"
          style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "8px 16px", borderRadius: 8,
            background: T.surface, border: `1px solid ${T.border}`,
            fontFamily: T.fontBody, fontSize: "0.82rem", fontWeight: 600,
            color: T.muted, cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.65 : 1,
          }}
        >
          <RefreshCw size={14} className={loading ? "aqpl-spin" : ""} />
          {!isMobile && "Refresh"}
        </button>
      </div>

      {/* ── KPI Stats ────────────────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(140px, 1fr))",
        gap: isMobile ? 8 : 12,
        marginBottom: 20,
      }}>
        {KPI.map(({ label, value, accent }) => (
          <div key={label} style={{
            background: T.surface, borderRadius: 8,
            padding: isMobile ? "10px 12px" : "14px 16px",
            border: `1px solid ${T.border}`, borderLeft: `3px solid ${accent}`,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}>
            <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? "1.5rem" : "1.9rem", fontWeight: 700, color: T.dark, margin: 0, lineHeight: 1 }}>{value}</p>
            <p style={{ fontFamily: T.fontBody, fontSize: "0.59rem", letterSpacing: "0.1em",  color: T.muted, margin: "5px 0 0" }}>{label}</p>
          </div>
        ))}
      </div>

      {/* ── Filter tabs ──────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {FILTERS.map(({ value, label }) => {
          const cnt = value === "sent_to_client" ? counts.sent_to_client : counts[value] ?? 0;
          const active = filter === value;
          return (
            <button
              key={value}
              onClick={() => setFilter(value)}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "6px 14px", borderRadius: 6, cursor: "pointer",
                background: active ? T.dark : "transparent",
                color:      active ? T.white : T.muted,
                border:     active ? `1px solid ${T.dark}` : `1px solid ${T.border}`,
                fontFamily: T.fontBody, fontSize: "0.8rem", fontWeight: active ? 600 : 400,
                transition: "all 0.15s",
              }}
            >
              {isMobile && value !== "all" ? label.split(" ")[0] : label}
              {value !== "all" && (
                <span style={{
                  padding: "1px 6px", borderRadius: 99, fontSize: "0.68rem",
                  background: active ? "rgba(255,255,255,0.15)" : T.bg,
                  color: active ? T.white : T.muted,
                  fontFamily: T.fontBody,
                }}>{cnt}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Content ──────────────────────────────────────────────── */}
      {loading && items.length === 0 ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "72px 24px" }}>
          <RefreshCw size={26} className="aqpl-spin" style={{ color: T.mutedLight, marginRight: 12 }} />
          <span style={{ fontFamily: T.fontBody, fontSize: "0.9rem", color: T.muted }}>Loading quote pool…</span>
        </div>
      ) : isMobile ? (
        /* ── Mobile card list ── */
        <div>
          {filtered.length === 0 && !loading ? (
            <div style={{ textAlign: "center", padding: "48px 24px" }}>
              <FileText size={44} style={{ color: T.mutedLight, margin: "0 auto 12px", display: "block" }} />
              <p style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, margin: 0 }}>No quotes found</p>
              <p style={{ fontFamily: T.fontBody, fontSize: "0.82rem", color: T.muted, margin: "4px 0 0" }}>
                {filter === "all"
                  ? "No individual user quote requests have been submitted yet."
                  : `No quotes with status "${filter.replaceAll("_", " ")}".`}
              </p>
            </div>
          ) : (
            filtered.map((q) => (
              <QuoteMobileCard
                key={q.id}
                q={q}
                isExpanded={expandedId === q.id}
                onToggle={() => setExpandedId(expandedId === q.id ? null : q.id)}
                onUpload={() => setUploadTarget(q)}
                onSend={() => sendToClient(q.id)}
                busy={!!actionBusy[q.id]}
              />
            ))
          )}
        </div>
      ) : (
        /* ── Desktop table ── */
        <div style={{
          background: T.surface, borderRadius: 10,
          border: `1px solid ${T.border}`,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: T.bg, borderBottom: `2px solid ${T.border}` }}>
                  <th style={{ width: 40, padding: "10px 12px" }} />
                  {["Project", "Requester", "Submitted", "Status", "Quote File", "Actions"].map((h, i) => (
                    <th key={h} style={{
                      padding: "10px 16px", textAlign: i === 5 ? "right" : "left",
                      fontFamily: T.fontBody, fontSize: "0.59rem",
                      letterSpacing: "0.12em", 
                      color: T.muted, fontWeight: 600,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((q) => {
                  const isExpanded = expandedId === q.id;
                  const ci         = q.contactInfo || {};
                  const fullName   = [ci.firstName, ci.lastName].filter(Boolean).join(" ") || ci.email || "Unknown";
                  const busy       = !!actionBusy[q.id];
                  const hasFile    = !!q.fileUrl;
                  const isSent     = q.status === "sent_to_client" || q.status === "accepted";

                  return (
                    <React.Fragment key={q.id}>
                      <tr
                        className="aqpl-row"
                        style={{
                          borderBottom: `1px solid ${T.border}`,
                          background: isExpanded ? T.amberBg : T.surface,
                        }}
                      >
                        {/* Expand toggle */}
                        <td style={{ padding: "12px 12px" }}>
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : q.id)}
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "center",
                              width: 26, height: 26, borderRadius: 6,
                              background: isExpanded ? T.amberBg : T.bg,
                              border: `1px solid ${isExpanded ? T.amberBorder : T.border}`,
                              cursor: "pointer", color: isExpanded ? T.amber : T.muted,
                            }}
                          >
                            {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                          </button>
                        </td>

                        {/* Project */}
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, fontSize: "0.88rem" }}>
                            {q.project?.name || q.projectId}
                          </div>
                          <div style={{ fontFamily: T.fontBody, fontSize: "0.62rem", color: T.mutedLight, marginTop: 2 }}>
                            {q.id.slice(0, 12)}…
                          </div>
                        </td>

                        {/* Requester */}
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{
                              width: 28, height: 28, borderRadius: "50%",
                              background: T.bg, border: `1px solid ${T.border}`,
                              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}>
                              <User size={13} style={{ color: T.muted }} />
                            </div>
                            <div>
                              <div style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, fontSize: "0.84rem" }}>{fullName}</div>
                              {ci.email && <div style={{ fontFamily: T.fontBody, fontSize: "0.63rem", color: T.muted }}>{ci.email}</div>}
                            </div>
                          </div>
                        </td>

                        {/* Date */}
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: T.fontBody, fontSize: "0.82rem", color: T.dark }}>
                            <Calendar size={13} style={{ color: T.mutedLight }} />
                            {new Date(q.createdAt).toLocaleDateString()}
                          </div>
                          <div style={{ fontFamily: T.fontBody, fontSize: "0.61rem", color: T.muted, marginTop: 2 }}>
                            {new Date(q.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </td>

                        {/* Status */}
                        <td style={{ padding: "12px 16px" }}>
                          <StatusBadge s={q.status} />
                        </td>

                        {/* Quote file */}
                        <td style={{ padding: "12px 16px" }}>
                          {hasFile ? (
                            <a
                              href={q.fileUrl} target="_blank" rel="noreferrer"
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                fontFamily: T.fontBody, fontSize: "0.82rem",
                                color: T.amber, fontWeight: 700, textDecoration: "none",
                              }}
                            >
                              <Download size={14} /> Download PDF
                            </a>
                          ) : (
                            <span style={{ fontFamily: T.fontBody, fontSize: "0.82rem", color: T.mutedLight, fontStyle: "italic" }}>
                              Not uploaded
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, flexWrap: "wrap" }}>

                            {/* Upload / Replace */}
                            <button
                              onClick={() => setUploadTarget(q)}
                              disabled={busy}
                              className="aqpl-btn"
                              style={{
                                display: "flex", alignItems: "center", gap: 5,
                                padding: "5px 11px", borderRadius: 6,
                                background: T.bg, color: T.dark,
                                border: `1px solid ${T.border}`,
                                fontFamily: T.fontBody, fontSize: "0.77rem", fontWeight: 600,
                                cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1,
                              }}
                            >
                              <Upload size={12} />
                              {hasFile ? "Replace PDF" : "Upload PDF"}
                            </button>

                            {/* Send to Client */}
                            {hasFile && !isSent && (
                              <button
                                onClick={() => sendToClient(q.id)}
                                disabled={busy}
                                className="aqpl-btn"
                                style={{
                                  display: "flex", alignItems: "center", gap: 5,
                                  padding: "5px 11px", borderRadius: 6,
                                  background: T.dark, color: T.white,
                                  border: `1px solid ${T.dark}`,
                                  fontFamily: T.fontBody, fontSize: "0.77rem", fontWeight: 700,
                                  cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1,
                                }}
                              >
                                {busy ? <RefreshCw size={12} className="aqpl-spin" /> : <Send size={12} />}
                                Send to Client
                              </button>
                            )}

                            {/* Resend */}
                            {hasFile && isSent && (
                              <button
                                onClick={() => sendToClient(q.id)}
                                disabled={busy}
                                className="aqpl-btn"
                                style={{
                                  display: "flex", alignItems: "center", gap: 5,
                                  padding: "5px 11px", borderRadius: 6,
                                  background: T.bg, color: T.muted,
                                  border: `1px solid ${T.border}`,
                                  fontFamily: T.fontBody, fontSize: "0.77rem", fontWeight: 600,
                                  cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1,
                                }}
                              >
                                {busy ? <RefreshCw size={12} className="aqpl-spin" /> : <Send size={12} />}
                                Resend
                              </button>
                            )}

                            {/* Approve (pending, no file) */}
                            {q.status === "pending_review" && !hasFile && (
                              <button
                                onClick={() => updateStatus(q.id, "approved")}
                                disabled={busy}
                                className="aqpl-btn"
                                style={{
                                  display: "flex", alignItems: "center", gap: 5,
                                  padding: "5px 11px", borderRadius: 6,
                                  background: "rgba(34,197,94,0.1)", color: "#16a34a",
                                  border: "1px solid rgba(34,197,94,0.25)",
                                  fontFamily: T.fontBody, fontSize: "0.77rem", fontWeight: 600,
                                  cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1,
                                }}
                              >
                                <CheckCircle size={12} /> Approve
                              </button>
                            )}

                            {/* Hold */}
                            {(q.status === "approved" || q.status === "sent_to_client") && (
                              <button
                                onClick={() => updateStatus(q.id, "pending_review")}
                                disabled={busy}
                                className="aqpl-btn"
                                style={{
                                  display: "flex", alignItems: "center", gap: 5,
                                  padding: "5px 11px", borderRadius: 6,
                                  background: T.amberBg, color: T.amberDark,
                                  border: `1px solid ${T.amberBorder}`,
                                  fontFamily: T.fontBody, fontSize: "0.77rem", fontWeight: 600,
                                  cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1,
                                }}
                              >
                                <Clock size={12} /> Hold
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {isExpanded && <QuoteDetailRow q={q} />}
                    </React.Fragment>
                  );
                })}

                {!filtered.length && !loading && (
                  <tr>
                    <td colSpan={7} style={{ padding: "56px 24px", textAlign: "center" }}>
                      <FileText size={44} style={{ color: T.mutedLight, margin: "0 auto 12px", display: "block" }} />
                      <p style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, margin: 0 }}>No quotes found</p>
                      <p style={{ fontFamily: T.fontBody, fontSize: "0.82rem", color: T.muted, margin: "4px 0 0" }}>
                        {filter === "all"
                          ? "No individual user quote requests have been submitted yet."
                          : `No quotes with status "${filter.replaceAll("_", " ")}".`}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {uploadTarget && (
        <UploadModal
          quote={uploadTarget}
          onClose={() => setUploadTarget(null)}
          onSuccess={load}
          showToast={showToast}
        />
      )}
      <Toast toast={toast} />
    </div>
  );
}
